'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import { useLanguage } from '@/context/LanguageContext';

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

const demoCredentials = [
  { role: 'Admin', email: 'admin@servenest.io', password: 'Admin@2026' },
  { role: 'Customer', email: 'melissa.park@gmail.com', password: 'Customer@2026' },
];

interface LoginFormProps {
  onSwitchToSignup: () => void;
}

export default function LoginForm({ onSwitchToSignup }: LoginFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { t } = useLanguage();
  const { register, handleSubmit, setValue, formState: { errors }, setError } = useForm<LoginFormData>();

  const onSubmit = async (data: LoginFormData) => {
    setLoading(true);
    // TODO: POST /api/auth/login with credentials
    await new Promise((r) => setTimeout(r, 1500));

    const valid = demoCredentials.some(
      (c) => c.email === data.email && c.password === data.password
    );

    if (!valid) {
      setLoading(false);
      setError('root', {
        message: 'Invalid credentials — use the demo accounts below to sign in',
      });
      return;
    }

    setLoading(false);
    toast.success('Welcome back! Redirecting to your dashboard...');
    // TODO: redirect to /dashboard or /admin-panel based on role
  };

  const fillCredentials = (email: string, password: string) => {
    setValue('email', email);
    setValue('password', password);
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-display font-bold text-2xl text-foreground mb-1">{t('auth.welcomeBack')}</h1>
        <p className="text-muted-foreground text-sm">{t('auth.welcomeSubtitle')}</p>
      </div>

      {/* Social auth */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <button className="flex items-center justify-center gap-2 px-4 py-2.5 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-muted transition-colors">
          <svg width="18" height="18" viewBox="0 0 24 24">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          {t('auth.google')}
        </button>
        <button className="flex items-center justify-center gap-2 px-4 py-2.5 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-muted transition-colors">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
          </svg>
          {t('auth.github')}
        </button>
      </div>

      <div className="flex items-center gap-3 mb-6">
        <div className="flex-1 h-px bg-border" />
        <span className="text-xs text-muted-foreground">{t('auth.orEmail')}</span>
        <div className="flex-1 h-px bg-border" />
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {errors.root && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-start gap-2">
            <Icon name="ExclamationCircleIcon" size={16} className="text-destructive flex-shrink-0 mt-0.5" />
            <p className="text-destructive text-sm">{errors.root.message}</p>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-foreground mb-1">{t('auth.emailAddress')}</label>
          <input
            {...register('email', {
              required: t('auth.required'),
              pattern: { value: /^\S+@\S+\.\S+$/, message: 'Invalid email address' }
            })}
            type="email"
            placeholder="your@email.com"
            className={`w-full px-3 py-2.5 border rounded-lg text-sm outline-none transition-colors ${
              errors.email ? 'border-destructive bg-red-50' : 'border-border focus:border-primary'
            }`}
          />
          {errors.email && <p className="text-destructive text-xs mt-1">{errors.email.message}</p>}
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm font-medium text-foreground">{t('auth.password')}</label>
            <button type="button" className="text-xs text-primary hover:underline">{t('auth.forgotPassword')}</button>
          </div>
          <div className="relative">
            <input
              {...register('password', { required: t('auth.required') })}
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter your password"
              className={`w-full px-3 py-2.5 pr-10 border rounded-lg text-sm outline-none transition-colors ${
                errors.password ? 'border-destructive bg-red-50' : 'border-border focus:border-primary'
              }`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
            </button>
          </div>
          {errors.password && <p className="text-destructive text-xs mt-1">{errors.password.message}</p>}
        </div>

        <div className="flex items-center gap-2">
          <input
            {...register('rememberMe')}
            type="checkbox"
            id="rememberMe"
            className="w-4 h-4 accent-primary rounded"
          />
          <label htmlFor="rememberMe" className="text-sm text-muted-foreground cursor-pointer">
            {t('auth.rememberMe')}
          </label>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 bg-primary text-white font-semibold text-sm rounded-lg hover:bg-primary-light transition-all duration-150 active:scale-95 disabled:opacity-70 flex items-center justify-center gap-2"
          style={{ minHeight: '44px' }}
        >
          {loading ? (
            <>
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
              </svg>
              {t('auth.signingIn')}
            </>
          ) : (
            t('auth.signInButton')
          )}
        </button>
      </form>

      <p className="text-center text-sm text-muted-foreground mt-4">
        {t('auth.noAccount')}{' '}
        <button onClick={onSwitchToSignup} className="text-primary font-medium hover:underline">
          {t('auth.createFree')}
        </button>
      </p>

      {/* Demo credentials */}
      <div className="mt-6 border border-border rounded-xl overflow-hidden">
        <div className="bg-muted px-4 py-2.5 flex items-center gap-2">
          <Icon name="KeyIcon" size={14} className="text-muted-foreground" />
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('auth.demoAccounts')}</span>
        </div>
        <div className="divide-y divide-border">
          {demoCredentials.map((cred) => (
            <div key={`demo-${cred.role}`} className="flex items-center justify-between px-4 py-3">
              <div>
                <span className="text-xs font-semibold text-foreground">{cred.role}</span>
                <p className="text-xs text-muted-foreground font-mono">{cred.email}</p>
              </div>
              <button
                type="button"
                onClick={() => fillCredentials(cred.email, cred.password)}
                className="px-3 py-1.5 text-xs font-medium bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors flex items-center gap-1"
              >
                <Icon name="ArrowRightIcon" size={12} />
                {t('auth.use')}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}